import type { ChatQueueItem } from "@codvero/shared";
import { useMutation } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, Check, Loader2, X, XCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useConfirm } from "@/ui/ConfirmDialog.tsx";

import { chatsApi } from "@/api/chats.ts";
import { cn } from "@/lib/cn.ts";
import { Button } from "@/ui/primitives.tsx";

/** The sequential task list of a chat: queued prompts run one after another as runs finish. */
export function QueuePanel({ chatId, items, canEdit, onClose }: { chatId: string; items: ChatQueueItem[]; canEdit: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const remove = useMutation({ mutationFn: (id: string) => chatsApi.dequeue(chatId, id) });
  const { confirm } = useConfirm();
  const clear = useMutation({ mutationFn: (finishedOnly: boolean) => chatsApi.clearQueue(chatId, finishedOnly) });
  const askClearAll = async () => { if (await confirm({ title: t("confirm.clearQueueTitle"), message: t("confirm.clearQueueMsg"), confirmLabel: t("chat.queueClearAll"), danger: true })) clear.mutate(false); };
  const reorder = useMutation({ mutationFn: (ids: string[]) => chatsApi.reorderQueue(chatId, ids) });
  const queued = items.filter((i) => i.status === "queued");
  const move = (id: string, dir: -1 | 1) => {
    const ids = queued.map((i) => i.id);
    const i = ids.indexOf(id);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j]!, ids[i]!];
    reorder.mutate(ids);
  };
  const icon = (s: ChatQueueItem["status"]) =>
    s === "running" ? <Loader2 className="size-3.5 animate-spin text-warn" /> : s === "done" ? <Check className="size-3.5 text-accent" /> : s === "failed" ? <XCircle className="size-3.5 text-danger" /> : <span className="size-3.5 rounded-full border border-border" />;
  return (
    <section className="mt-3 rounded-xl border border-border bg-surface" aria-label={t("chat.taskList")}>
      <header className="flex items-center gap-2 border-b border-border px-3 py-2">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-fg-muted">{t("chat.taskList")}</h3>
        <span className="text-xs text-fg-muted">{queued.length > 0 ? `${queued.length} ${t("chat.queued")}` : ""}</span>
        <div className="ms-auto flex items-center gap-1">
          {canEdit && items.some((i) => i.status === "done" || i.status === "failed") && <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => clear.mutate(true)}>{t("chat.queueClearDone")}</Button>}
          {canEdit && queued.length > 0 && <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => void askClearAll()}>{t("chat.queueClearAll")}</Button>}
          <button type="button" className="text-fg-muted hover:text-fg" onClick={onClose} aria-label={t("common.close")}><X className="size-4" /></button>
        </div>
      </header>
      {items.length === 0 ? (
        <p className="px-3 py-3 text-xs text-fg-muted">{t("chat.queueEmpty")}</p>
      ) : (
        <ol className="max-h-48 overflow-y-auto py-1">
          {items.map((it, idx) => (
            <li key={it.id} className={cn("flex items-center gap-2 px-3 py-1.5 text-sm", it.status === "done" && "text-fg-muted line-through decoration-border", it.status === "failed" && "text-danger")}>
              <span className="w-5 text-end text-xs text-fg-muted">{idx + 1}</span>
              {icon(it.status)}
              <span className="min-w-0 flex-1 truncate" title={it.text}>{it.text}</span>
              {it.attachments.length > 0 && <span className="text-[10px] text-fg-muted">📎 {it.attachments.length}</span>}
              <span className="text-[10px] text-fg-muted">{t(`chat.status.${it.status}`)}</span>
              {canEdit && it.status === "queued" && (
                <span className="flex items-center">
                  <button type="button" className="p-0.5 text-fg-muted hover:text-fg disabled:opacity-30" disabled={queued[0]?.id === it.id} onClick={() => move(it.id, -1)} aria-label="up"><ArrowUp className="size-3.5" /></button>
                  <button type="button" className="p-0.5 text-fg-muted hover:text-fg disabled:opacity-30" disabled={queued.at(-1)?.id === it.id} onClick={() => move(it.id, 1)} aria-label="down"><ArrowDown className="size-3.5" /></button>
                  <button type="button" className="p-0.5 text-fg-muted hover:text-danger" onClick={() => remove.mutate(it.id)} aria-label={t("common.remove")}><X className="size-3.5" /></button>
                </span>
              )}
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}
